import { useEffect, useState } from "react";
import { d, fmtPhone } from "../../lib/dialer";

/**
 * H14/H14-bis (Ticket 4b-2, corrección: Founder/gerente probaron con central real -- durante la CONSULTA el
 * destino nunca recibe `incoming` de SIP.js, solo GET /transfers/incoming; la alerta grande no se enteraba).
 * Alerta GRANDE de transferencia entrante. Superposición centrada (no bloquea colgar: el teléfono sigue
 * debajo) con la ficha básica del cliente. `incoming` (SIP.js, con `invitation`) y `preview` (GET
 * /transfers/incoming, ya resuelto por PhoneContext con pickTransferPreview) son DOS fuentes de la MISMA
 * alerta -- cualquiera de las dos la muestra; `incoming` es quien de verdad permite Atender/Rechazar (viene con
 * la invitación SIP real). Si solo hay `preview` (la consulta ya te está sonando el teléfono pero SIP.js
 * todavía no entregó la invitación a esta pestaña), los botones se avisan como no disponibles AÚN en vez de
 * fingir una acción que no existe -- apenas llegue `incoming` pasan a ser reales, sin que el usuario haga nada.
 *
 * LO QUE NO SE MUESTRA (limitación honesta, ver reporte de entrega): notas/pedidos/correo del cliente salen de
 * GET /calls/{id}/context, que exige haber ACEPTADO la llamada (acceso "verde"); antes de aceptar solo hay lo
 * mismo que ya leía <IncomingPreview> en PhoneDevice.jsx -- nombre, teléfono y la transcripción -- más el
 * tiempo que lleva esperando. No se inventó un endpoint nuevo.
 */
export default function TransferAlertOverlay({ incoming, preview, onAccept, onReject }) {
  const callId = preview?.call_id || null;
  const [segs, setSegs] = useState([]);
  const [elapsed, setElapsed] = useState("00:00");

  useEffect(() => {
    if (!callId) { setSegs([]); return undefined; }
    let alive = true;
    const tick = async () => {
      try { const c = await d.get(`/calls/${callId}`); if (alive) setSegs((c.data?.transcript || []).slice(-5)); }
      catch (e) { /* sin acceso todavía: se queda sin texto */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(id); };
  }, [callId]);

  useEffect(() => {
    if (!preview?.started_at) { setElapsed("00:00"); return undefined; }
    const start = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(preview.started_at) ? preview.started_at : `${preview.started_at}Z`);
    const tick = () => {
      if (Number.isNaN(start)) return;
      const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
      setElapsed(`${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [preview?.started_at]);

  const clientName = preview?.client_name || (incoming?.client && incoming.client !== "-" ? incoming.client : "Cliente");
  const phone = preview?.phone || incoming?.phone;
  const from = incoming?.from || preview?.from || "Un agente";
  const canAct = !!incoming;   // solo hay invitación SIP real (acceptIncoming/rejectIncoming) cuando esto es cierto

  return (
    <div className="ta-backdrop" data-testid="transfer-alert" role="alertdialog" aria-live="assertive" aria-label="Te transfieren una llamada">
      <div className="ta-card">
        <div className="ta-title">📞 Te transfieren una llamada</div>
        <div className="ta-from">{from} te la está pasando</div>
        <div className="ta-client">
          <b>{clientName}</b>
          {phone && phone !== "-" && <span className="ta-phone">{fmtPhone(phone) || phone}</span>}
        </div>
        <div className="ta-row"><span>Esperando</span><b>{elapsed}</b></div>
        {segs.length > 0 && (
          <div className="ta-transcript" data-testid="transfer-alert-transcript">
            <div className="ta-mini-label">LO QUE SE HA HABLADO</div>
            {segs.map((s, i) => <div key={s.seq ?? i} className="ta-seg"><b>{s.speaker === "cliente" ? (s.speaker_label || "Cliente") : "Agente"}:</b> {s.text}</div>)}
          </div>
        )}
        {canAct ? (
          <div className="ta-actions">
            <button type="button" className="ta-btn reject" onClick={onReject} data-testid="transfer-alert-reject">Rechazar</button>
            <button type="button" className="ta-btn accept" onClick={onAccept} data-testid="transfer-alert-accept">Atender</button>
          </div>
        ) : (
          <div className="ta-wait" data-testid="transfer-alert-waiting">Tu teléfono está sonando: contesta ahí. Esta pantalla pasa a Atender/Rechazar en cuanto llegue la llamada.</div>
        )}
      </div>
      <style>{`
        .ta-backdrop{position:fixed;inset:0;z-index:10100;display:flex;align-items:center;justify-content:center;
          background:rgba(5,6,9,.72);pointer-events:none}
        .ta-card{pointer-events:auto;width:min(420px,92vw);border-radius:22px;padding:22px 24px;color:#F6F6F7;
          background:linear-gradient(180deg,#171208,#0b0c11);border:2px solid #FFA300;
          box-shadow:0 40px 90px -30px #000,0 0 0 6px rgba(255,163,0,.18);
          font:13px/1.45 Inter,sans-serif;animation:ta-pulse 1.1s ease-in-out infinite}
        .ta-title{font:800 20px Sora,Inter,sans-serif;letter-spacing:-.02em;margin-bottom:4px}
        .ta-from{color:#9A9BA4;margin-bottom:14px;font-size:13px}
        .ta-client{display:flex;align-items:baseline;gap:10px;font-size:17px;margin-bottom:6px}
        .ta-phone{color:#9A9BA4;font:600 13px 'JetBrains Mono',monospace}
        .ta-row{display:flex;justify-content:space-between;color:#9A9BA4;font-size:12.5px;margin-bottom:10px}
        .ta-row b{color:#F6F6F7;font-family:'JetBrains Mono',monospace}
        .ta-transcript{background:rgba(0,0,0,.3);border-radius:12px;padding:10px 12px;margin-bottom:14px;max-height:120px;overflow-y:auto}
        .ta-mini-label{font:700 9.5px 'JetBrains Mono',monospace;letter-spacing:.14em;color:#ffb780;margin-bottom:6px}
        .ta-seg{font-size:12px;margin-bottom:3px;color:#d6d7de}
        .ta-actions{display:flex;gap:10px}
        .ta-btn{flex:1;padding:13px 14px;border-radius:13px;font:800 14px Inter,sans-serif;border:0;cursor:pointer}
        .ta-btn.reject{background:rgba(255,255,255,.08);color:#F6F6F7;border:1px solid rgba(255,255,255,.14)}
        .ta-btn.accept{background:linear-gradient(135deg,#FF6A00,#FFA300);color:#1a0d00}
        .ta-wait{font:600 12.5px Inter,sans-serif;color:#ffd9a0;background:rgba(255,163,0,.1);border:1px dashed rgba(255,163,0,.4);
          border-radius:12px;padding:10px 12px;text-align:center}
        @keyframes ta-pulse{0%,100%{border-color:#FFA300;box-shadow:0 40px 90px -30px #000,0 0 0 6px rgba(255,163,0,.18)}
          50%{border-color:#FF4D4D;box-shadow:0 40px 90px -30px #000,0 0 0 10px rgba(255,77,77,.22)}}
        @media (prefers-reduced-motion:reduce){.ta-card{animation:none;border-color:#FFA300}}
      `}</style>
    </div>
  );
}
